// ═══════════════════════════════════════════════════════════════
// DESK WRITES — how Lumen's desk updates land on a document, and how
// Lumen finds out what actually landed. Pure functions, no React.
// ═══════════════════════════════════════════════════════════════

export const DESK_DOC_CHAR_LIMIT = 4000;
export const DESK_TOTAL_CHAR_LIMIT = 12000;

/** Desk HTML → readable plain text (keeps line structure). */
export function deskHtmlToText(html) {
  return String(html || "")
    .replace(/<li[^>]*>/gi, "\n- ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:p|div|h[1-6]|tr|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&(?:quot|#34);/gi, '"')
    .replace(/&(?:apos|#39);/gi, "'")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const normalizeTitle = (title) => String(title || "").trim().toLowerCase();

const BLOCK_TAG_RE = /<\/?(?:p|h[1-6]|li|ul|ol|blockquote|div|pre|table|thead|tbody|tr|td|th|hr|br)\b[^>]*>/gi;

function decodeEntities(text) {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&(?:quot|#34);/gi, '"')
    .replace(/&(?:apos|#39);/gi, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&amp;/gi, "&");
}

/** One line of text as compared: letter case, curly quotes, dash styles and spacing don't count as edits. */
function comparableLine(htmlSegment) {
  return decodeEntities(String(htmlSegment || "").replace(/<[^>]+>/g, ""))
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
    .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * A doc as its block-level lines of text (paragraphs, headings, list items). Comparing whole
 * blocks keeps "Budget" from matching inside "Budgeting", and ignores markup differences
 * between Lumen's HTML and the editor's (<li>x</li> vs <li><p>x</p></li>). A leading block
 * that is just the doc's title is dropped — unless it is the only block (a whiteboard card sent
 * to the desk is titled with its own text).
 */
function textBlocks(html, title) {
  const blocks = String(html || "")
    .split(BLOCK_TAG_RE)
    .map(comparableLine)
    .filter(Boolean);
  if (blocks.length > 1 && title && blocks[0] === comparableLine(title)) blocks.shift();
  return blocks;
}

/** Comparable key for one line of text — what the preview uses to spot changed lines. */
export const deskLineKey = comparableLine;

/** Lines of the current doc that the draft no longer contains (for the "Removed" list in a preview). */
export function removedDeskLines(currentHtml, draftHtml, limit = 5) {
  const draft = textBlocks(draftHtml);
  const currentRaw = String(currentHtml || "")
    .split(BLOCK_TAG_RE)
    .map((seg) => ({ key: comparableLine(seg), text: decodeEntities(seg.replace(/<[^>]+>/g, "")).replace(/\s+/g, " ").trim() }))
    .filter((l) => l.key);
  return currentRaw.filter((l) => !draft.some((d) => d.includes(l.key))).slice(0, limit).map((l) => l.text);
}

/**
 * A short typed answer to an open desk question ("update", "keep both", "skip") — the same as
 * clicking that button, so it isn't sent to Lumen as a new request.
 */
export function matchDeskChoiceReply(text, kind) {
  const t = String(text || "").trim().toLowerCase().replace(/[.!\s]+$/g, "").replace(/^(yes|yeah|yep|ok|okay|sure)[,\s]+/, "");
  if (/^(skip|no|nope|cancel|never ?mind|leave it|don'?t)$/.test(t)) return "dismiss";
  if (/^(keep both|both|keep both versions|make a new doc|new doc|new document)$/.test(t)) return "new";
  if (kind === "version" && /^(update|update (it|the doc|doc|the document)|use (it|the new (one|version))|replace( it)?|swap( it)? in)$/.test(t)) return "replace";
  if (kind === "placement" && /^(add|add (it|to existing|to (the|that) doc)|add it to (the|that) doc)$/.test(t)) return "append";
  return null;
}

/** True when `needle` appears in `haystack` as an unbroken run of blocks. */
function containsBlockRun(haystack, needle) {
  if (!needle.length || needle.length > haystack.length) return false;
  for (let i = 0; i + needle.length <= haystack.length; i++) {
    let j = 0;
    while (j < needle.length && haystack[i + j] === needle[j]) j++;
    if (j === needle.length) return true;
  }
  return false;
}

/**
 * True when every block of `kept` survives in `next`, in the same order (new blocks may sit in
 * between). A block survives if a block of `next` still holds its whole text — so adding a
 * sentence to the end of an existing paragraph still counts as keeping that paragraph.
 */
function keepsEveryBlock(next, kept) {
  let i = 0;
  for (const block of next) {
    if (i < kept.length && block.includes(kept[i])) i++;
  }
  return i === kept.length;
}

/** Share of incoming blocks that already exist somewhere in the doc. */
function overlapRatio(existingBlocks, incomingBlocks) {
  if (!incomingBlocks.length) return 0;
  const have = new Set(existingBlocks);
  return incomingBlocks.filter((b) => have.has(b)).length / incomingBlocks.length;
}

function stripLeadingH1(html) {
  return String(html || "").replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, "");
}

/** The desk item in a Lumen reply's surface_updates ("desk" in any letter case). */
export function findDeskUpdate(surfaceUpdates) {
  return (surfaceUpdates || []).find((u) => String(u?.surface || "").trim().toLowerCase() === "desk") || null;
}

/** Title Lumen meant for this update: explicit title, else its first h1/h2. */
export function titleFromDeskUpdate(update) {
  if (update?.title && String(update.title).trim()) return String(update.title).trim();
  const m = String(update?.content || "").match(/<h[12][^>]*>(.*?)<\/h[12]>/i);
  return m ? m[1].replace(/<[^>]+>/g, "").trim() : "";
}

export function findDocByTitle(docs, title) {
  const wanted = normalizeTitle(title);
  if (!wanted) return null;
  return (docs || []).find((d) => normalizeTitle(d.title) === wanted) || null;
}

/** Which existing doc an append is aimed at: explicit doc_id, then title, then the room's only doc. */
export function resolveDeskTarget(docs, update) {
  const list = docs || [];
  const wantedId = update?.doc_id || update?.docId || null;
  if (wantedId) {
    const byId = list.find((d) => d.id === wantedId || d.sourceSurfaceItemId === wantedId);
    if (byId) return byId;
  }
  const byTitle = findDocByTitle(list, update?.title);
  if (byTitle) return byTitle;
  return list.length === 1 ? list[0] : null;
}

/**
 * Adds incoming HTML to an existing doc without ever dropping what is there.
 * - incoming already in the doc → "unchanged" (a retry or a repeat)
 * - incoming keeps every line of the doc, in order, with nothing removed (words may be added to a
 *   line) → "updated": Lumen sent the whole doc with new content placed where it fits
 * - otherwise → "appended" to the bottom
 */
export function mergeIntoDeskDoc(existingHtml, incomingHtml, title) {
  const existing = String(existingHtml || "");
  const incoming = stripLeadingH1(incomingHtml).trim();
  const existingBlocks = textBlocks(existing, title);
  const incomingBlocks = textBlocks(incoming, title);

  if (!incomingBlocks.length) return { status: "unchanged", content: existing };
  if (containsBlockRun(existingBlocks, incomingBlocks)) return { status: "unchanged", content: existing };
  if (!existingBlocks.length || keepsEveryBlock(incomingBlocks, existingBlocks)) return { status: "updated", content: incoming };
  return { status: "appended", content: existing + incoming };
}

/**
 * An append that mostly repeats the doc without containing all of it — typically Lumen resending
 * an older copy after the user edited. Appending it would duplicate the doc, so the user decides.
 */
function isNearResend(existingHtml, incomingHtml, title) {
  const incomingBlocks = textBlocks(stripLeadingH1(incomingHtml), title);
  return incomingBlocks.length >= 2 && overlapRatio(textBlocks(existingHtml, title), incomingBlocks) >= 0.5;
}

/** Same text, ignoring markup — used to check a doc hasn't been edited before undoing a replace. */
export function sameDeskText(aHtml, bHtml) {
  const a = textBlocks(aHtml);
  const b = textBlocks(bHtml);
  return a.length === b.length && a.every((block, i) => block === b[i]);
}

export function uniqueDeskTitle(docs, title) {
  const base = String(title || "").trim() || "Untitled";
  if (!findDocByTitle(docs, base)) return base;
  let n = 2;
  while (findDocByTitle(docs, `${base} (${n})`)) n += 1;
  return `${base} (${n})`;
}

/**
 * Decides what a desk write should do before any state changes.
 *
 * mode "append": add to the target doc. No target and an empty desk → create one;
 *   no target on a desk with several docs → failed (never guess which one); mostly a repeat
 *   of the doc → conflict, unless forceAppend (the user already chose "Add to existing").
 * mode "new": create a doc. A same-title doc is never silently overwritten:
 *   identical content → unchanged; onTitleConflict "reject" (Lumen) → a full resend that
 *   keeps everything → updated, anything else → conflict for the user to decide;
 *   onTitleConflict "unique" (default, user actions) → a new doc with a "(2)" title.
 * mode "replace": swap the target doc's content for this version — only ever run after the user
 *   picked "Replace" (or Undo). Needs a clear target; same text → unchanged.
 *
 * Returns { kind: "create" | "update" | "unchanged" | "conflict" | "failed", ... }.
 */
export function planDeskWrite({ docs, update, mode, onTitleConflict = "unique", forceAppend = false }) {
  const list = docs || [];
  const title = titleFromDeskUpdate(update);
  if (!textBlocks(update?.content).length) return { kind: "failed", reason: "empty_content", title };

  if (mode === "replace") {
    const target = resolveDeskTarget(list, { ...update, title });
    if (!target) return { kind: "failed", reason: "target_not_found", title };
    if (sameDeskText(target.content, update.content)) return { kind: "unchanged", docId: target.id, title: target.title };
    return { kind: "update", status: "replaced", docId: target.id, title: target.title };
  }

  if (mode === "append") {
    const target = resolveDeskTarget(list, { ...update, title });
    if (!target) {
      if (list.length === 0) return { kind: "create", title: title || "Untitled" };
      return { kind: "failed", reason: "target_not_found", title };
    }
    const merged = mergeIntoDeskDoc(target.content, update.content, target.title);
    if (merged.status === "unchanged") return { kind: "unchanged", docId: target.id, title: target.title };
    if (merged.status === "appended" && !forceAppend && isNearResend(target.content, update.content, target.title)) {
      return { kind: "conflict", reason: "repeats_document", docId: target.id, title: target.title };
    }
    return { kind: "update", status: merged.status, docId: target.id, title: target.title };
  }

  const match = findDocByTitle(list, title);
  if (!match) return { kind: "create", title: title || "Untitled" };

  const merged = mergeIntoDeskDoc(match.content, update.content, match.title);
  if (merged.status === "unchanged") return { kind: "unchanged", docId: match.id, title: match.title };
  if (onTitleConflict === "reject") {
    if (merged.status === "updated") return { kind: "update", status: "updated", docId: match.id, title: match.title };
    return { kind: "conflict", docId: match.id, title: match.title };
  }
  return { kind: "create", title: uniqueDeskTitle(list, title) };
}

/** True once a doc's content really contains what was written to it. */
export function deskDocContains(docContent, incomingHtml, title) {
  return containsBlockRun(textBlocks(docContent, title), textBlocks(stripLeadingH1(incomingHtml), title));
}

// ── Lumen context ─────────────────────────────────────────────

/** Keeps the start and the end of a long doc — appends land at the bottom, so Lumen must see it. */
function clipMiddle(text, cap) {
  if (text.length <= cap) return text;
  const head = Math.floor(cap * 0.4);
  const tail = cap - head;
  return `${text.slice(0, head)}\n…(middle of document not shown)…\n${text.slice(text.length - tail)}`;
}

/** Desk documents as Lumen sees them in its system prompt, with the doc_id it must use to target one. */
export function formatDeskDocuments(docs) {
  if (!docs?.length) return "none";
  let remaining = DESK_TOTAL_CHAR_LIMIT;
  const blocks = [];
  for (let i = 0; i < docs.length; i++) {
    if (remaining <= 0) {
      blocks.push(`(${docs.length - i} more document(s) on this desk, not shown)`);
      break;
    }
    const title = docs[i].title || "Untitled";
    const body = clipMiddle(deskHtmlToText(docs[i].content), Math.min(DESK_DOC_CHAR_LIMIT, remaining));
    remaining -= body.length;
    const header = `[doc_id: ${docs[i].id}] "${title}"`;
    blocks.push(body ? `${header}:\n${body}` : `${header}: (empty)`);
  }
  return `(full text of each document on this desk follows)\n\n${blocks.join("\n\n")}`;
}

// ── Receipts ──────────────────────────────────────────────────
// A receipt is the app's record of what a desk write actually did. It is shown to the user
// under Lumen's reply and fed back to Lumen on later turns, so neither has to take the
// model's word for it.

const FAILURE_REASONS = {
  target_not_found: "I couldn't tell which document to add it to.",
  empty_content: "There was nothing to add.",
  no_desk_update: "Nothing was sent to the desk.",
  no_room: "You need to be in a room for the desk.",
  verify_failed: "It didn't show up when I checked.",
  dismissed: "You chose not to add it.",
  choice_closed: "The choice closed before anything was added.",
};

export function isDeskReceiptSuccess(receipt) {
  return ["created", "appended", "updated", "replaced", "unchanged"].includes(receipt?.status);
}

export function describeDeskReceipt(receipt) {
  if (!receipt) return "";
  const title = receipt.title ? `“${receipt.title}”` : "the document";
  const notSaved = receipt.saved === false ? " It hasn't saved to your account yet." : "";
  switch (receipt.status) {
    case "created":
      return receipt.keptBoth
        ? `Kept both. The new version is ${title}, and the original is unchanged.${notSaved}`
        : `Added ${title} to your desk.${notSaved}`;
    case "appended":  return `Added to the bottom of ${title} on your desk.${notSaved}`;
    case "updated":   return `Updated ${title} on your desk.${notSaved}`;
    case "replaced":  return `Updated ${title} with the new version.${notSaved}`;
    case "undone":    return `Undone. ${title} is back to the old version.`;
    case "unchanged": return `${title} already has this. Nothing new was added.`;
    case "pending":   return "Waiting for you to choose where this goes on the desk.";
    default:
      return `This didn't make it to your desk. ${FAILURE_REASONS[receipt.reason] || "Something went wrong."}`;
  }
}

/**
 * While a write waits for the user's choice nothing has changed yet, so a reply that opens with
 * "Done." or "Updated." would be a false confirmation. Drops that opener; the rest stays.
 */
export function softenPendingReply(text) {
  const softened = String(text || "").replace(/^\s*(?:done|updated|all set)\s*[.!]\s*/i, "");
  return softened.trim() ? softened : text;
}

// Receipts once rode inside Lumen's own past messages; the model then copied the note into new
// replies. Anything shaped like one is removed from what Lumen says and from the history it reads.
const RECEIPT_ECHO_RE = /\[\s*Desk (?:receipt|result)s?\b[^\]]*\]/gi;

export function stripReceiptEcho(text) {
  return String(text ?? "").replace(RECEIPT_ECHO_RE, "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Chat history for the model: role and text only, with no receipt-shaped notes to imitate. */
export function chatHistoryForModel(messages) {
  return (messages || []).map((m) => ({
    role: m.role,
    content: m.role === "assistant" ? stripReceiptEcho(m.content) || "..." : m.content,
  }));
}

/**
 * "Desk Writes That Did Not Land" for Lumen's system prompt: recent writes that failed, are still
 * waiting for the user, were undone, or haven't saved — the cases where Lumen's own memory of
 * "I added it" would be wrong. Writes that landed aren't listed: Desk Documents already shows their
 * text, and listing them made Lumen hold back on new writes. Kept out of the chat history so
 * there's no pattern to copy.
 */
export function formatDeskResults(messages, limit = 6) {
  const missed = (messages || [])
    .filter((m) => m.role === "assistant" && m.deskReceipt && (!isDeskReceiptSuccess(m.deskReceipt) || m.deskReceipt.saved === false))
    .slice(-limit);
  if (!missed.length) return "none";
  return missed
    .map((m) => {
      const said = stripReceiptEcho(m.content).replace(/\s+/g, " ");
      const quote = said.length > 60 ? `${said.slice(0, 60)}…` : said;
      return `After your reply "${quote}": ${describeDeskReceipt(m.deskReceipt)}`;
    })
    .join(" | ");
}

/**
 * Cleans a saved conversation as it loads:
 * - a reply saved while its desk choice was still open can't be answered any more (the choice
 *   doesn't survive a reload or a room change), so it must not keep saying "waiting";
 * - an older Lumen reply that copied a receipt note into its text gets that note removed.
 */
export function settleStaleDeskReceipts(messages) {
  return (messages || []).map((m) => {
    let next = m;
    if (m.role === "assistant" && typeof m.content === "string") {
      const cleaned = stripReceiptEcho(m.content);
      if (cleaned !== m.content.trim()) next = { ...next, content: cleaned || "..." };
    }
    if (m.deskReceipt?.status === "pending") {
      next = { ...next, deskReceipt: { status: "failed", reason: "choice_closed", title: m.deskReceipt.title || null } };
    }
    return next;
  });
}
